import React from "react";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { RootDispatch, RootState } from "../../../store";
import {
  createRawMaterialThunk,
  updateRawMaterialThunk,
} from "../../../store/slices/warehouseSlice";
import {
  IWarehouseRawMaterial,
  UNITS,
  UnitType,
} from "../../../models/warehouseRawMaterial.model";
import Select from "react-select";

interface RawMaterialModalProps {
  isOpen: boolean;
  onClose: () => void;
  editMode: boolean;
  getRawMaterials: () => Promise<void>;
  selectedItem: IWarehouseRawMaterial | null;
  rawMaterialForm: Partial<IWarehouseRawMaterial>;
  setRawMaterialForm: React.Dispatch<
    React.SetStateAction<Partial<IWarehouseRawMaterial>>
  >;
  rawMaterialPage: number;
  itemsPerPage: number;
  searchQuery: string;
}

const MATERIAL_TYPES: string[] = [
  "Raw Material",
  "Component",
  "Sub-Assembly",
  "Consumable",
  "Packing Material",
];

const USAGE_TYPES = [
  { value: "warehouse", label: "Warehouse" },
  { value: "factory", label: "Factory" },
];

const UNIT_TYPES_WHEN_CREATING: { value: string; label: string }[] = [
  { value: "mg", label: "Weight (Kilogram, Gram)" },
  { value: "mm", label: "Length (Centimeter, Kilometer)" },
  { value: "ml", label: "Liquid (Litre, Milliliter)" },
  { value: "piece", label: "Piece" },
];

const Button: React.FC<{
  type: string;
  onClick: (e?: React.FormEvent) => void | Promise<void>;
  className: string;
  disabled?: boolean;
  children: React.ReactNode;
}> = ({ type, onClick, className, disabled = false, children }) => (
  <button
    type={type as "button" | "submit"}
    onClick={onClick}
    disabled={disabled}
    className={`px-4 py-2 rounded-md font-medium transition-colors flex items-center justify-center min-w-[100px]
      ${disabled ? "cursor-not-allowed opacity-70" : ""}
      ${className}`}
  >
    {children}
  </button>
);

const RawMaterialModal: React.FC<RawMaterialModalProps> = ({
  isOpen,
  onClose,
  editMode,
  selectedItem,
  rawMaterialForm,
  setRawMaterialForm,
  rawMaterialPage,
  itemsPerPage,
  searchQuery,
  getRawMaterials,
}) => {
  const dispatch = useDispatch<RootDispatch>();
  const { loading } = useSelector((state: RootState) => state.warehouse);
  const serialNumbersSupported = rawMaterialForm.unit === "piece";

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setRawMaterialForm((prev) => ({
      ...prev,
      [name]:
        name === "approxDeliveryDays" || name === "gst"
          ? value === ""
            ? undefined
            : Math.abs(Number(value))
          : value,
      ...(name === "unit" && value !== "piece"
        ? { requireSerialNumber: false }
        : {}),
    }));
  };

  const handleSelectChange = (selected: { value: string } | null) => {
    setRawMaterialForm((prev) => ({
      ...prev,
      type: selected?.value || "",
    }));
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    try {
      if (
        !rawMaterialForm.type ||
        !rawMaterialForm.name ||
        !rawMaterialForm.sku ||
        !rawMaterialForm.unit ||
        !rawMaterialForm.usageType ||
        rawMaterialForm.approxDeliveryDays === undefined ||
        rawMaterialForm.gst === undefined
      ) {
        toast.error("Please fill all required fields.");
        return;
      }
      if (editMode && selectedItem) {
        await dispatch(
          updateRawMaterialThunk({
            id: selectedItem._id,
            data: {
              type: rawMaterialForm.type,
              name: rawMaterialForm.name,
              sku: rawMaterialForm.sku,
              unit: rawMaterialForm.unit as UnitType,
              usageType: rawMaterialForm.usageType,
              approxDeliveryDays: rawMaterialForm.approxDeliveryDays,
              gst: rawMaterialForm.gst ?? 0,
              hsn: rawMaterialForm.hsn ?? "",
              requireSerialNumber:
                serialNumbersSupported &&
                (rawMaterialForm.requireSerialNumber ?? false),
            },
          }),
        ).unwrap();
      } else {
        await dispatch(
          createRawMaterialThunk({
            type: rawMaterialForm.type,
            name: rawMaterialForm.name,
            sku: rawMaterialForm.sku,
            unit: rawMaterialForm.unit as UnitType,
            usageType: rawMaterialForm.usageType,
            approxDeliveryDays: rawMaterialForm.approxDeliveryDays,
            gst: rawMaterialForm.gst ?? 0,
            hsn: rawMaterialForm.hsn ?? "",
            requireSerialNumber:
              serialNumbersSupported &&
              (rawMaterialForm.requireSerialNumber ?? false),
          }),
        ).unwrap();
      }
      await getRawMaterials();
      onClose();
    } catch (err: any) {
      toast.error(
        err?.message ||
          `Failed to ${editMode ? "update" : "create"} raw material`,
      );
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[10000] animate-fade-in">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden animate-scale-in">
        <div className="bg-gray-100 px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">
            {editMode ? "Edit" : "Add"} Raw Material
          </h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-800 focus:outline-none"
            aria-label="Close modal"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <div className="flex flex-col h-[70vh]">
          <div className="flex-1 p-6 overflow-y-auto">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Type *
                  </label>
                  <Select
                    name="type"
                    value={MATERIAL_TYPES.map((type) => ({
                      value: type,
                      label: type,
                    })).find((option) => option.value === rawMaterialForm.type)}
                    onChange={handleSelectChange}
                    options={MATERIAL_TYPES.map((type) => ({
                      value: type,
                      label: type,
                    }))}
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    className="w-full"
                    classNamePrefix="react-select"
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: "#cbd5e0",
                        "&:hover": { borderColor: "#0077C0" },
                        boxShadow: "none",
                        borderRadius: "0.5rem",
                        padding: "0.25rem",
                      }),
                      menu: (base) => ({
                        ...base,
                        zIndex: 9999,
                        borderRadius: "0.5rem",
                      }),
                      option: (base, { isFocused, isSelected }) => ({
                        ...base,
                        backgroundColor: isSelected
                          ? "#0077C0"
                          : isFocused
                            ? "#f1f5f9"
                            : "white",
                        color: isSelected ? "white" : "#1f2937",
                        "&:hover": { backgroundColor: "#f1f5f9" },
                      }),
                    }}
                    isSearchable
                    isClearable
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={rawMaterialForm.name || ""}
                    onChange={handleChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      SKU *
                    </label>
                    <input
                      type="text"
                      name="sku"
                      value={rawMaterialForm.sku || ""}
                      onChange={handleChange}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      HSN Code
                    </label>
                    <input
                      type="text"
                      name="hsn"
                      value={rawMaterialForm.hsn || ""}
                      onChange={handleChange}
                      placeholder="Enter HSN code"
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Unit Type *
                    </label>
                    <select
                      name="unit"
                      value={rawMaterialForm.unit || ""}
                      onChange={handleChange}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      required
                    >
                      <option value="">Select Unit</option>
                      {(editMode ? UNITS : UNIT_TYPES_WHEN_CREATING).map(
                        (unit) => (
                          <option key={unit.value} value={unit.value}>
                            {unit.label}
                          </option>
                        ),
                      )}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      GST (%) *
                    </label>
                    <input
                      type="number"
                      name="gst"
                      value={rawMaterialForm.gst ?? ""}
                      onChange={handleChange}
                      min={0}
                      max={100}
                      step={1}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Approx. Delivery Days *
                  </label>
                  <input
                    type="number"
                    name="approxDeliveryDays"
                    value={rawMaterialForm.approxDeliveryDays ?? ""}
                    onChange={handleChange}
                    min={0}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  />
                </div>
                <div className="flex items-center gap-3 p-3 border border-slate-300 rounded-lg">
                  <input
                    type="checkbox"
                    id="requireSerialNumber"
                    checked={
                      serialNumbersSupported &&
                      (rawMaterialForm.requireSerialNumber ?? false)
                    }
                    disabled={!serialNumbersSupported}
                    onChange={(e) =>
                      setRawMaterialForm((prev) => ({
                        ...prev,
                        requireSerialNumber: e.target.checked,
                      }))
                    }
                    className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 transition-all"
                  />
                  <label
                    htmlFor="requireSerialNumber"
                    className="text-sm font-medium text-slate-700 cursor-pointer"
                  >
                    Require Serial Number
                  </label>
                  {!serialNumbersSupported && (
                    <span className="text-xs text-slate-500">
                      Available only for materials measured in pieces.
                    </span>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Usage Type *
                  </label>
                  <select
                    name="usageType"
                    value={rawMaterialForm.usageType || ""}
                    onChange={handleChange}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    required
                  >
                    <option value="">Select Usage</option>
                    {USAGE_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </form>
          </div>
          <div className="bg-gray-100 px-6 py-4 flex justify-end space-x-3">
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className={`bg-[#0077C0] text-white ${
                loading ? "hover:bg-[#0077C0]" : "hover:bg-[#005B90]"
              }`}
            >
              {loading ? (
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              ) : editMode ? (
                "Update"
              ) : (
                "Create"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RawMaterialModal;
